import {
  Analytics,
  Apps as AppsIcon,
  ArrowBack,
  Campaign,
  CardMembership,
  DnsOutlined,
  Inventory,
  People,
  RequestQuote,
  Settings,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DiscordIcon from "../../img/discord.svg";
import { hostname, User } from "../../utils";
import DNSSetupDialog from "./DNSSetupDialog";
import WhitelabelAnalytics from "./WhitelabelAnalytics";
import WhitelabelApps from "./WhitelabelApps";
import WhitelabelCustomers from "./WhitelabelCustomers";
import WhitelabelEditorDialog, {
  WhitelabelInstance,
} from "./WhitelabelEditorDialog";
import WhitelabelNotifications from "./WhitelabelNotifications";
import WhitelabelOwnersDialog from "./WhitelabelOwnersDialog";
import WhitelabelQuotes from "./WhitelabelQuotes";
import WhitelabelServicePackages from "./WhitelabelServicePackages";
import WhitelabelSubscriptions from "./WhitelabelSubscriptions";

export default function WhitelabelDetail({
  currentUser,
  getToken,
  mode,
  whitelabelId,
  eventListener,
  onBack,
}: {
  currentUser: User;
  getToken: () => Promise<string>;
  mode: string;
  whitelabelId?: string;
  eventListener?: Function;
  onBack?: () => void;
}) {
  const { companyId: urlWhitelabelId, subScreenName: section } = useParams();
  const navigate = useNavigate();

  // Use prop whitelabelId if provided, otherwise fall back to URL param
  const finalWhitelabelId = whitelabelId || urlWhitelabelId;

  const [whitelabel, setWhitelabel] = useState<WhitelabelInstance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [dnsSetupOpen, setDnsSetupOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Determine current tab based on URL section
  useEffect(() => {
    if (section === "customers") {
      setCurrentTab(1);
    } else if (section === "subscriptions") {
      setCurrentTab(2);
    } else if (section === "service-packages") {
      setCurrentTab(3);
    } else if (section === "quotes") {
      setCurrentTab(4);
    } else if (section === "notifications") {
      setCurrentTab(5);
    } else if (section === "analytics") {
      setCurrentTab(6);
    } else if (section === "apps") {
      setCurrentTab(7);
    } else {
      setCurrentTab(0); // Overview/Settings
    }
  }, [section]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    const sections = [
      "",
      "customers",
      "subscriptions",
      "service-packages",
      "quotes",
      "notifications",
      "analytics",
      "apps",
    ];
    const newSection = sections[newValue];
    if (finalWhitelabelId && !whitelabelId) {
      // Only navigate if we're using URL-based routing (no onBack prop)
      if (newSection) {
        navigate(`/dashboard/whitelabel/${finalWhitelabelId}/${newSection}`);
      } else {
        navigate(`/dashboard/whitelabel/${finalWhitelabelId}`);
      }
    }
  };

  const loadWhitelabel = async () => {
    if (!finalWhitelabelId) {
      setError("No whitelabel ID provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const currentToken = await getToken();
      console.log("Loading whitelabel:", finalWhitelabelId);

      const response = await axios.get(
        `${hostname}/whitelabel/${finalWhitelabelId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      console.log("Whitelabel response:", response.data);
      setWhitelabel(response.data);
    } catch (err: any) {
      console.error("Error loading whitelabel:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to load whitelabel instance";
      setError(errorMessage);

      // If it's a 404 or access denied, redirect back to list after showing error
      if (err.response?.status === 404 || err.response?.status === 403) {
        setTimeout(() => {
          if (onBack) {
            onBack();
          } else {
            navigate("/dashboard/whitelabel");
          }
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    console.log(
      "WhitelabelDetail useEffect - currentUser.uid:",
      currentUser.uid,
      "finalWhitelabelId:",
      finalWhitelabelId
    );
    if (currentUser.uid && finalWhitelabelId) {
      loadWhitelabel();
    }
  }, [currentUser.uid, finalWhitelabelId]);

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error: {error}
        </Alert>
        <Button
          onClick={() =>
            onBack ? onBack() : navigate("/dashboard/whitelabel")
          }
        >
          Back
        </Button>
      </Box>
    );
  }

  if (!whitelabel) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Whitelabel instance not found.</Typography>
        <Button
          onClick={() =>
            onBack ? onBack() : navigate("/dashboard/whitelabel")
          }
          sx={{ mt: 2 }}
        >
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() =>
            onBack ? onBack() : navigate("/dashboard/whitelabel")
          }
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {whitelabel.settings?.title || "Untitled"} - Whitelabel Instance
        </Typography>
        <Chip
          label={whitelabel.is_owner ? "Owner" : "Shared"}
          size="small"
          color={whitelabel.is_owner ? "primary" : "default"}
        />
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<Settings />} label="Overview & Settings" />
          <Tab icon={<People />} label="Customers" />
          <Tab icon={<CardMembership />} label="Subscriptions" />
          <Tab icon={<Inventory />} label="Service Packages" />
          <Tab icon={<RequestQuote />} label="Quotes" />
          <Tab icon={<Campaign />} label="Notifications" />
          <Tab icon={<Analytics />} label="Analytics" />
          <Tab icon={<AppsIcon />} label="Apps" />
        </Tabs>
      </Paper>

      {currentTab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Manage your whitelabel instance settings, branding, and
            configuration.
          </Alert>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Instance Information
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Title
                </Typography>
                <Typography>
                  {whitelabel.settings?.title || "Untitled"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Domain
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>
                    {whitelabel.domain || "Not configured"}
                  </Typography>
                  {whitelabel.domain && (
                    <Button
                      size="small"
                      startIcon={<DnsOutlined />}
                      onClick={() => setDnsSetupOpen(true)}
                    >
                      DNS Setup
                    </Button>
                  )}
                </Box>
              </Box>
              {whitelabel.domain && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Marketing / Homepage
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      The root page can be configured in Settings to show either
                      the marketing homepage or login page.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Login Page
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/login`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/login
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Signup Page
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/signup`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/signup
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Pricing Page
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/pricing`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/pricing
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Security Page
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/security`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/security
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Roadmap Page
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/roadmap`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/roadmap
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Privacy Policy
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/privacy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/privacy
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Terms of Service
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/terms`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/terms
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Video Training
                    </Typography>
                    <Typography
                      component="a"
                      href={`https://${encodeURIComponent(whitelabel.domain)}/training`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      https://{whitelabel.domain}/training
                    </Typography>
                  </Box>
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="body2" color="text.secondary">
                      These default pages are meant to help you get started and
                      offer limited customization. We encourage you to create
                      your own landing pages for a fully branded experience.
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      <strong>Legal Disclaimer:</strong> The default Terms of
                      Service and Privacy Policy pages are generic templates
                      provided for convenience only. They do not constitute
                      legal advice and are provided "as is" without any warranty
                      or liability. You are solely responsible for ensuring your
                      legal documents comply with applicable laws and
                      regulations. We recommend consulting with a qualified
                      legal professional.
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>

          <Box
            component="a"
            href="https://discord.gg/U8qbJsZPtC"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              textDecoration: "none",
              color: "primary.main",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <img
              src={DiscordIcon}
              alt="Discord"
              style={{ width: 20, height: 20 }}
            />
            <Typography variant="body2">Join our white-label owners community</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Settings />}
              onClick={() => setEditorOpen(true)}
            >
              Edit Settings
            </Button>
            {whitelabel.is_owner && (
              <Button
                variant="outlined"
                startIcon={<People />}
                onClick={() => setOwnersOpen(true)}
              >
                Manage Owners
              </Button>
            )}
          </Box>
        </Box>
      )}

      {currentTab === 1 && (
        <WhitelabelCustomers
          currentUser={currentUser}
          getToken={getToken}
          whitelabelId={finalWhitelabelId!}
          companyProvisioningEnabled={whitelabel?.companyProvisioningEnabled || false}
        />
      )}

      {currentTab === 2 && (
        <WhitelabelSubscriptions
          whitelabelId={finalWhitelabelId!}
          currentUser={currentUser}
          getToken={getToken}
          eventListener={eventListener}
        />
      )}

      {currentTab === 3 && (
        <WhitelabelServicePackages
          whitelabelId={finalWhitelabelId!}
          currentUser={currentUser}
          getToken={getToken}
        />
      )}

      {currentTab === 4 && (
        <WhitelabelQuotes
          whitelabelId={finalWhitelabelId!}
          currentUser={currentUser}
          getToken={getToken}
        />
      )}

      {currentTab === 5 && (
        <WhitelabelNotifications
          whitelabelId={finalWhitelabelId!}
          currentUser={currentUser}
          getToken={getToken}
        />
      )}

      {currentTab === 6 && (
        <WhitelabelAnalytics
          currentUser={currentUser}
          getToken={getToken}
          whitelabelId={finalWhitelabelId!}
        />
      )}

      {currentTab === 7 && (
        <WhitelabelApps
          currentUser={currentUser}
          getToken={getToken}
          whitelabelId={finalWhitelabelId!}
        />
      )}

      <WhitelabelEditorDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          loadWhitelabel(); // Refresh data after editing
        }}
        getToken={getToken}
        mode={mode}
        whitelabel={whitelabel}
      />

      <WhitelabelOwnersDialog
        open={ownersOpen}
        onClose={() => {
          setOwnersOpen(false);
        }}
        getToken={getToken}
        whitelabel={whitelabel}
        eventListener={eventListener}
      />

      <DNSSetupDialog
        open={dnsSetupOpen}
        onClose={() => {
          setDnsSetupOpen(false);
        }}
        whitelabel={whitelabel}
        getToken={getToken}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
